import type { BaseEditor, BaseElement, Range } from 'slate'
import { Editor, Element, Node, Transforms } from 'slate'
import { ReactEditor } from 'slate-react'
import type { RefObject } from 'react'
import { useEffect, useRef } from 'react'

import { BASE_BLOCK_ELEMENT_TYPES } from './serialization'

declare module 'slate' {
  interface CustomTypes {
    Editor: BaseEditor &
      ReactEditor & {
        useLastSelection: (editor: ReactEditor) => RefObject<Range | undefined>
        isValidRange: (editor: ReactEditor, range: Range) => boolean
        isEmptyLine: (editor: ReactEditor) => boolean
      }

    // TODO: Typings have changed with Slate 0.61.
    //
    // The following types should be more specific. See also:
    // https://docs.slatejs.org/concepts/12-typescript
    //
    // To improve, we could extract specific typings from the elements and
    // nodes that are used in ./serialization.js
    Element: BaseElement & { type: string }
  }
}

export function withUtils(editor: Editor): Editor {
  function useLastSelection(editor: ReactEditor) {
    const lastSelection = useRef<Range>(undefined)

    useEffect(() => {
      if (editor.selection) lastSelection.current = editor.selection
    }, [editor, editor.selection])

    return lastSelection
  }

  function isValidRange(editor: Editor, range: Range) {
    try {
      ReactEditor.toDOMRange(editor, range)
      return true
    } catch {
      return false
    }
  }

  function isEmptyLine(editor: Editor) {
    return (
      Array.from(
        Editor.nodes(editor, {
          match: (node) => Element.isElement(node) && BASE_BLOCK_ELEMENT_TYPES.includes(node.type),
        }),
      )
        .map(([node]) => Node.string(node))
        .join('') === ''
    )
  }

  editor.useLastSelection = useLastSelection
  editor.isValidRange = isValidRange
  editor.isEmptyLine = isEmptyLine

  return editor
}

// https://github.com/ianstormtaylor/slate/issues/419#issuecomment-590135015
export function withSingleLine(editor) {
  const { normalizeNode } = editor

  editor.normalizeNode = ([node, path]) => {
    if (path.length === 0) {
      if (editor.children.length > 1) {
        Transforms.mergeNodes(editor)
      }
    }

    return normalizeNode([node, path])
  }

  return editor
}
